/* eslint-disable max-lines */
import React, {
  useEffect, useMemo, useRef, useState, 
} from 'react'
import maplibregl from 'maplibre-gl'
import { useReactToPrint } from 'react-to-print';
import FloodsLayer from '@App/components/FloodsLayer/FloodsLayer';
import { useSpecificArea } from '@utils/queries/area.queries';
import { useSelector } from 'react-redux';
import { createLayers } from '@App/components/CompareModeMap/compare-mode-map.utils';
import { center } from '@turf/turf';
import BuildingDamagesLayer from '@App/components/BuildingDamagesLayer/BuildingDamagesLayer';
import UserDamageArea from '@App/components/UserDamageArea/UserDamageArea';
import CostGraph from '@App/components/BuildingDamages/CostGraph/CostGraph';
import CostTable from '@App/components/BuildingDamages/CostTable/CostTable';
import { fitToUserArea } from '@library/MaplibreMap/maplibre-map.utils';

import './print.styl'
import { Portal } from '@nazka/nazka.mapframe.components';
import Legend from '../../App/components/Legend/Legend';
import {
  calculateCostPerClass, calculateTotalDamage, calculateUACDetail,
} from '../../App/components/BuildingDamages/building-damages.utils';
import RiskDistributionGraph from '../../App/components/RiskDistributionGraph/RiskDistributionGraph';
import PEValueGraph from '../../App/components/PEValueGraph/PEValueGraph';
import FlashFloodDamagesLayer from '../../App/components/FlashFloodDamagesLayer/FlashFloodDamagesLayer';

function Print({ changeTriggerStatus, isPrintTriggered }) {
  const contentRef = useRef(null);
  const [mapOne, setMapOne] = useState(null);
  const [mapTwo, setMapTwo] = useState(null);
  const currentSelectedDamageLayer = useSelector(state => state.layerSelector.currentSelectedDamageLayer)
  const {
    features, totalBuildingCount, areaId, modelId, scenario, customFlood,
  } = currentSelectedDamageLayer;

  const { data: specificArea } = useSpecificArea(areaId)
  const totalDamage = calculateTotalDamage(currentSelectedDamageLayer?.features);
  const costPerClass = useMemo(() => calculateCostPerClass(features), [features]);
  const userSelectedMode = useSelector(state => state.maplibreMap.userSelectedMode);
  const costPerClassDetail = useMemo(() => calculateUACDetail(features), [features])

  const reactToPrintFn = useReactToPrint({
    contentRef,
    onAfterPrint: () => {
      changeTriggerStatus(false);
    },
  });

  useEffect(() => {
    const { coordinates } = center(specificArea?.feature).geometry

    const locationMap = new maplibregl.Map({
      container: 'location-map',
      attributionControl: false,
      style: `https://api.maptiler.com/maps/basic-v2-light/style.json?key=${import.meta.env.VITE_MAPTILER_TOKEN}`,
      center: coordinates,
      zoom: 12,
      interactive: false,
    });
    const locationMapTwo = new maplibregl.Map({
      container: 'location-map-two',
      attributionControl: false,
      style: `https://api.maptiler.com/maps/basic-v2-light/style.json?key=${import.meta.env.VITE_MAPTILER_TOKEN}`,
      center: coordinates,
      zoom: 12,
      interactive: false,
    });
  
    locationMap.on('load', () => {
      setMapOne(locationMap);
      createLayers(locationMap)
      fitToUserArea(locationMap, specificArea)
    });
    locationMapTwo.on('load', () => {
      setMapTwo(locationMapTwo);
      createLayers(locationMapTwo)
      fitToUserArea(locationMapTwo, specificArea)
    });
  
    return () => {
      if (locationMap) {
        setMapOne(null)
        locationMap.remove();
      }
      if (locationMapTwo) {
        setMapTwo(null)
        locationMapTwo.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (!mapOne || !mapTwo) return
    const printImg = () => {
      if (mapOne && mapTwo) {
        const canvasOne = document.querySelector('.print__first-map__wrapper .maplibregl-canvas');
        const canvasTwo = document.querySelector('.print__second-map__wrapper .maplibregl-canvas');
        if (!canvasOne) return

        const img = document.createElement('img');
        const imgTwo = document.createElement('img');
        img.className = 'maplibregl-canvas-img';
        imgTwo.className = 'maplibregl-canvas-imgTwo';

        const rect = canvasOne.getBoundingClientRect();
        img.style.width = `${rect.width}px`;
        img.style.height = `${rect.height}px`;
        const rectTwo = canvasTwo.getBoundingClientRect();
        imgTwo.style.width = `${rectTwo.width}px`;
        imgTwo.style.height = `${rectTwo.height}px`;

        // img.style.width = '400px';
        // img.style.height = '400px';
        // imgTwo.style.width = '400px';
        // imgTwo.style.height = '400px';

        canvasOne.parentNode.insertBefore(img, canvasOne);
        canvasTwo.parentNode.insertBefore(imgTwo, canvasTwo);

        const dataURL = canvasOne.toDataURL('image/png');
        const dataURLTwo = canvasTwo.toDataURL('image/png');

        img.src = dataURL;
        img.style.display = 'block';
        imgTwo.src = dataURLTwo;
        imgTwo.style.display = 'block';

        canvasOne.style.display = 'none';
        canvasTwo.style.display = 'none';
        reactToPrintFn()
      }
    }

    let mapOneIdle = false;
    let mapTwoIdle = false;
  
    const tryPrint = () => {
      if (mapOneIdle && mapTwoIdle) {
        printImg();
      }
    };
  
    const onMapOneIdle = () => {
      mapOneIdle = true;
      tryPrint();
    };
  
    const onMapTwoIdle = () => {
      mapTwoIdle = true;
      tryPrint();
    };
  
    mapOne.once('idle', onMapOneIdle);
    mapTwo.once('idle', onMapTwoIdle);
  }, [mapOne, mapTwo, reactToPrintFn])
  
  return (
    <Portal>
      <div className="print" ref={contentRef}>
        <h3>BUILDSPACE Service 5 Urban Flood Analysis and Resilience</h3>
        <h4>Map</h4>
        <div className="print__first-map__wrapper">
          <div className="print__map-wrapper">
            <div className="print__map" id="location-map" />
          </div>
          <div>
            <Legend forPrint />
          </div>
        </div>
        <div className="print__second-map__wrapper">
          <div className="print__map-wrapper">
            <div className="print__map" id="location-map-two" />
          </div>
          <div>
            <Legend forPrint customLegendSelection="cost" />
          </div>
        </div>

        <div className="print__graphs-tables">
          {userSelectedMode === 'Flash flood' ? (
            <div className="print__graphs-tables__flash-flood">
              <h3>Area flash flood risk</h3>
              <PEValueGraph currentSelectedDamageLayer={currentSelectedDamageLayer} />
              <h3>Building risk distribution</h3>
              <RiskDistributionGraph currentSelectedDamageLayer={currentSelectedDamageLayer} />
            </div>
          ) : (
            <div>
              {modelId === 'uac' ? (
                <div className="print__graphs-tables__uac">
                  <h3>Costs per damage class</h3>
                  <CostTable data={costPerClass} forPrint />
                  <CostGraph data={costPerClass} totalDamage={totalDamage} width={380} />
                  <h3>Costs per land use class</h3>
                  <CostTable data={costPerClassDetail} forPrint />
                  <CostGraph
                    data={costPerClassDetail}
                    totalDamage={totalDamage}
                  />
                </div>
              ) : (
                <div>
                  <div className="print__table-graph__wrapper">
                    <h3>Costs per damage class</h3>
                    <CostTable data={costPerClass} forPrint />
                    <CostGraph data={costPerClass} totalDamage={totalDamage} width={380} />
                  </div>
                </div>
              )}
             
            </div>
          )}
        </div>
        {mapOne && (
        <>
            {userSelectedMode === 'River flood' && (
              <>
                <BuildingDamagesLayer
                  map={mapOne}
                  currentSelectedDamageLayer={currentSelectedDamageLayer}
                  customLegendSelection="classes"
                />
                <FloodsLayer
                  map={mapOne}
                  scenario={scenario}
                  selectedValue={customFlood}
                />
              </>
            )}
          <UserDamageArea map={mapOne} areaId={areaId} />
            {userSelectedMode === 'Flash flood'
              && <FlashFloodDamagesLayer map={mapOne} currentSelectedDamageLayer={currentSelectedDamageLayer} />}
        </>    
        )}
        {mapTwo && (
        <>            
            {userSelectedMode === 'River flood' && (
              <>
                <BuildingDamagesLayer
                  map={mapTwo}
                  currentSelectedDamageLayer={currentSelectedDamageLayer}
                  customLegendSelection="cost"
                />
                <FloodsLayer
                  map={mapTwo}
                  scenario={scenario}
                  selectedValue={customFlood}
                />  
              </>
            )}
          <UserDamageArea map={mapTwo} areaId={areaId} />
            {userSelectedMode === 'Flash flood'
              && (
              <FlashFloodDamagesLayer
                map={mapTwo}
                currentSelectedDamageLayer={currentSelectedDamageLayer}
                customLegendSelection="cost"
              />
              )}
        </>    
        )}
      </div>
    </Portal>
  )
}

export default Print